/**
 * Connection-type compatibility rules for interactive protocol DAG editing.
 */

export type ProtocolNodeKind = "criterion" | "atom" | "gate";

export type ProtocolConnectionType =
  | "HAS_ATOM"
  | "MEMBER_OF_GATE"
  | "DEPENDS_ON"
  | "APPLIES_IF"
  | "EXCEPTS"
  | "OVERRIDES";

const PAIR_TYPES: Record<
  ProtocolConnectionType,
  [ProtocolNodeKind, ProtocolNodeKind]
> = {
  HAS_ATOM: ["criterion", "atom"],
  MEMBER_OF_GATE: ["atom", "gate"],
  DEPENDS_ON: ["atom", "atom"],
  APPLIES_IF: ["atom", "atom"],
  EXCEPTS: ["atom", "atom"],
  OVERRIDES: ["atom", "atom"],
};

const EXECUTION_TYPES = new Set<ProtocolConnectionType>([
  "MEMBER_OF_GATE",
  "DEPENDS_ON",
  "APPLIES_IF",
]);

/**
 * Return connection types allowed between two node kinds.
 *
 * @param fromKind - Source node kind
 * @param toKind - Target node kind
 * @returns Compatible connection type names
 */
export function compatibleConnectionTypes(
  fromKind: ProtocolNodeKind,
  toKind: ProtocolNodeKind,
): ProtocolConnectionType[] {
  return (
    Object.entries(PAIR_TYPES) as Array<
      [ProtocolConnectionType, [ProtocolNodeKind, ProtocolNodeKind]]
    >
  )
    .filter(([, [from, to]]) => from === fromKind && to === toKind)
    .map(([type]) => type);
}

/**
 * Whether a connection participates in execution-cycle checks.
 *
 * @param connectionType - Connection type name
 * @returns True when the edge is an execution dependency
 */
export function isExecutionConnection(connectionType: string): boolean {
  return EXECUTION_TYPES.has(connectionType as ProtocolConnectionType);
}

/**
 * Expected endpoint kinds for a connection type.
 *
 * @param connectionType - Connection type name
 * @returns Endpoint kinds, or null when unknown
 */
export function endpointKindsForConnection(
  connectionType: ProtocolConnectionType,
): [ProtocolNodeKind, ProtocolNodeKind] | null {
  return PAIR_TYPES[connectionType] ?? null;
}
